import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { FormProps } from '@leafwell/components';
import type { SelectFieldControlledProps } from './select-field';
import type {
  ConditionsStudiesQuery as SelectData,
  StudiesByYearQuery,
} from 'graphql/research/types-and-hooks';
import type {
  DashboardFilters,
  GetDashboardInitialData,
} from 'lib/queries/dashboard';
import type { QueryError } from 'lib/api';
import type { GetResultsAggregateCount } from 'lib/queries/studies';
import type { SelectMultipleGetOptions } from './select-multiple';

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  useUniqueId,
  useFormContext,
  useToast,
  Button,
  Form,
  Link,
  Wrap,
} from '@leafwell/components';
import {
  ConditionsStudiesDocument,
  OrganSystemsStudiesDocument,
  refetchStudiesByYearQuery as fetchStudiesByYearQuery,
} from 'graphql/research/types-and-hooks';
import { DASHBOARD_FILTERS, mapQueryFilters } from 'lib/queries/dashboard';

import { useMatchMedia } from 'hooks/useMatchMedia';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { queryResearch } from 'lib/apollo';
import { useDasboardContext } from 'contexts/dashboard-context';
import { getResultsAggregateCount } from 'lib/queries/studies';
import SelectMultiple from './select-multiple';
import { debounce } from 'lib/utils/debounce';

type DashboardFormProps = FormProps & {
  initialData: GetDashboardInitialData;
  onToggleForm: (open: boolean) => void;
};

const DashboardSelect: React.FC<
  Pick<SelectFieldControlledProps<any>, 'label' | 'name' | 'placeholder'> & {
    initialData?: SelectFieldControlledProps<any>['options'];
    limit?: number;
  }
> = memo(({ label, name, initialData = [], limit, placeholder }) => {
  const client = useApolloClient();
  const { addToast } = useToast();

  const dataSettings = useMemo(() => {
    switch (name) {
      case DASHBOARD_FILTERS.CANNABINOIDS:
        return {
          key: 'id',
          labelKey: 'name',
          groupKey: 'category',
        };
      case DASHBOARD_FILTERS.CONDITIONS:
        return {
          query: ConditionsStudiesDocument,
          key: 'id',
          labelKey: 'name',
        };
      case DASHBOARD_FILTERS.LOCATIONS:
        return {
          key: 'id',
          labelKey: 'location',
        };
      case DASHBOARD_FILTERS.ORGAN_SYSTEMS:
        return {
          query: OrganSystemsStudiesDocument,
          key: 'id',
          labelKey: 'name',
        };
      case DASHBOARD_FILTERS.RESULTS:
        return {
          key: 'result',
          labelKey: 'result',
        };
      case DASHBOARD_FILTERS.TYPES:
        return {
          key: 'typeofstudy',
          labelKey: 'typeofstudy',
        };
      default:
        return null;
    }
  }, [name]);

  const getOptions: SelectMultipleGetOptions = useCallback(
    async ({ offset, search }) => {
      try {
        const { data } = await queryResearch<
          Pick<SelectData, 'pagination'> & { nodes: unknown[] }
        >(client as ApolloClient<NormalizedCacheObject>, {
          query: dataSettings.query,
          variables: { offset, search: search ? `%${search}%` : '%' },
        });

        const offsetToInt =
          typeof offset === 'number' ? offset : parseInt(offset);

        if (data?.nodes?.length) {
          return {
            data: data.nodes,
            variables: {
              offset,
              search,
              hasNextPage: data.pagination.aggregate.count > offsetToInt,
            },
          };
        }

        return { data: [], variables: {} };
      } catch (error) {
        addToast({
          content: error?.message,
          key: 'dashboard-select-error',
          type: 'negative',
          shouldRemove: true,
        });
      }
    },
    [client, dataSettings?.query],
  );

  return (
    <SelectMultiple
      {...{
        dataSettings: {
          ...dataSettings,
          ...(dataSettings?.query ? { getOptions } : {}),
        },
        name,
        label,
        initialData,
        limit,
        placeholder,
      }}
    />
  );
});

const DashboardFormContent: React.FC<DashboardFormProps> = memo(
  ({ initialData, onSubmit, onToggleForm }) => {
    const sectionId = useUniqueId('dashboard-form');
    const [visible, toggleForm] = useState(true);
    const { translations } = useWordPressContext();
    const { handleSubmit, watch } = useFormContext();
    const [isMediumScreen] = useMatchMedia(['(min-width: 1024px)'], [true]);

    const hideForm = useCallback(() => {
      toggleForm(false);
      onToggleForm(false);

      window.document.body.classList.remove('overflow-hidden');
    }, [onToggleForm]);

    const showForm = useCallback(() => {
      toggleForm(true);
      onToggleForm(true);

      window.document.body.classList.add('overflow-hidden');
    }, [onToggleForm]);

    const toggleFormVisibility = useCallback((display: boolean) => {
      toggleForm(display);
    }, []);

    useEffect(() => {
      let subscription = null;
      const unsubscribe = () => subscription?.unsubscribe();

      if (!isMediumScreen) {
        unsubscribe();
      } else {
        subscription = watch((_, { type }) => {
          if (type === 'change') {
            // @ts-ignore
            debounce(handleSubmit(onSubmit)(), 150);
          }
        });
      }

      return () => unsubscribe();
    }, [handleSubmit, isMediumScreen, watch]);

    useEffect(() => {
      toggleFormVisibility(isMediumScreen);
    }, [isMediumScreen]);

    return (
      <Wrap
        {...{
          className: 'py-3',
          size: 'large',
          spacer: false,
          tagName: 'div',
        }}
      >
        <Button
          {...{
            'aria-controls': sectionId,
            'aria-expanded': visible,
            className: 'w-full block lg:hidden',
            label: translations['Show Filters'],
            onClick: showForm,
            rounded: true,
            size: 'small',
            variant: 'secondary',
          }}
        />
        <section
          {...{
            'aria-hidden': !visible,
            className: [
              'content-start bg-gray-100 fixed top-0 left-0 z-40 h-full w-full transition-all overflow-auto lg:z-10 lg:overflow-visible lg:static',
              visible ? '' : 'invisible opacity-0',
            ].join(' '),
            id: sectionId,
          }}
        >
          <div className="p-5 sm:py-10 grid gap-3 content-start lg:p-0 lg:grid-flow-col lg:auto-cols-fr lg:content-end lg:items-end">
            <Link
              {...{
                'aria-controls': sectionId,
                'aria-expanded': visible,
                className: 'block lg:hidden justify-self-end',
                onClick: hideForm,
                variant: 'underline',
              }}
            >
              {translations['Close']}
            </Link>
            {/* @ts-ignore */}
            <DashboardSelect
              {...{
                label: translations['Types of Study'],
                name: DASHBOARD_FILTERS.TYPES,
                initialData: initialData[DASHBOARD_FILTERS.TYPES],
                placeholder: translations['Select types'],
              }}
            />
            {/* @ts-ignore */}
            <DashboardSelect
              {...{
                label: translations['Results'],
                name: DASHBOARD_FILTERS.RESULTS,
                initialData: initialData[DASHBOARD_FILTERS.RESULTS],
                placeholder: translations['Select results'],
              }}
            />
            {/* @ts-ignore */}
            <DashboardSelect
              {...{
                label: translations['Organ Systems'],
                name: DASHBOARD_FILTERS.ORGAN_SYSTEMS,
                initialData: initialData[DASHBOARD_FILTERS.ORGAN_SYSTEMS].nodes,
                limit: 6,
                placeholder: translations['Select organ systems'],
              }}
            />
            {/* @ts-ignore */}
            <DashboardSelect
              {...{
                label: translations['Medical Conditions'],
                name: DASHBOARD_FILTERS.CONDITIONS,
                initialData: initialData[DASHBOARD_FILTERS.CONDITIONS].nodes,
                limit: 16,
                placeholder: translations['Select conditions'],
              }}
            />
            {/* @ts-ignore */}
            <DashboardSelect
              {...{
                label: translations['Cannabinoids & Endocannabinoids'],
                name: DASHBOARD_FILTERS.CANNABINOIDS,
                initialData: initialData[DASHBOARD_FILTERS.CANNABINOIDS],
                placeholder: translations['Select cannabinoids'],
              }}
            />
            {/* @ts-ignore */}
            <DashboardSelect
              {...{
                label: translations['Countries'],
                name: DASHBOARD_FILTERS.LOCATIONS,
                initialData: initialData[DASHBOARD_FILTERS.LOCATIONS],
                placeholder: translations['Select countries'],
              }}
            />
            <Button
              {...{
                className: 'w-full block lg:hidden mt-3',
                label: translations['Filter results'],
                onClick: hideForm,
                rounded: true,
                size: 'small',
                type: 'submit',
                variant: 'secondary',
              }}
            />
          </div>
        </section>
      </Wrap>
    );
  },
);

const DashboardForm: React.FC<Pick<DashboardFormProps, 'onToggleForm'>> = ({
  onToggleForm,
}) => {
  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const { addToast } = useToast();
  const { dispatch, initialData } = useDasboardContext();

  const onSubmit = useCallback(
    async (values: DashboardFilters) => {
      try {
        const mappedfilters = mapQueryFilters(values);
        const newFilters = Object.values(mappedfilters).length
          ? mappedfilters
          : null;

        if (newFilters) {
          dispatch({ type: 'updateFilters', value: newFilters });

          const { data: studiesByYear, error: errorStudiesByYear } =
            await queryResearch<StudiesByYearQuery>(
              client,
              fetchStudiesByYearQuery({ filters: newFilters }),
            );

          if (errorStudiesByYear) {
            throw errorStudiesByYear;
          }

          const results = await getResultsAggregateCount({
            client,
            filters: newFilters,
          });

          if ((results as QueryError)?.error) {
            throw results;
          }

          dispatch({
            type: 'updateData',
            value: {
              results: results as GetResultsAggregateCount,
              studies: {
                byYear: studiesByYear.nodes,
                paginated: [],
                total: studiesByYear.nodes.length,
              },
            },
          });
        } else {
          dispatch({ type: 'reset' });
        }
      } catch (error) {
        console.log('dashboard form error', error);
        dispatch({ type: 'toggleLoading', value: false });
        addToast({
          content: error.message,
          key: 'dashboard-form-error',
          type: 'negative',
          shouldRemove: true,
        });
      }
    },
    [dispatch],
  );

  return (
    <Form
      {...{
        defaultValues: Object.values(DASHBOARD_FILTERS).reduce(
          (values, key) => ({ ...values, [key]: '' }),
          {},
        ),
        onSubmit,
      }}
    >
      <DashboardFormContent {...{ initialData, onSubmit, onToggleForm }} />
    </Form>
  );
};

export default DashboardForm;
